import { useMemo, useState } from "react";
import {
  Drawer,
  Table,
  Button,
  Space,
  Input,
  Switch,
  Select,
  Popconfirm,
  Typography,
  message,
  Row,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation } from "@apollo/client";
import {
  EMAILS_NOTIFICATIONS,
  CREATE_EMAILS_NOTIFICATION,
  UPDATE_EMAILS_NOTIFICATION,
  GET_LOCATIONS,
} from "./requests";

const { Text } = Typography;

const EmailsNotificationsDrawer = ({
  open,
  setOpen,
  user,
}: any) => {
  const isAdmin = user?.type === "admin";

  const [pageConfig, setPageConfig] = useState({
    page: 1,
    pageSize: 10,
  });

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [form, setForm] = useState({
    to: "",
    cc: "",
    isDefault: false,
    location: null as string | null,
  });

  const { data, loading, refetch } = useQuery(EMAILS_NOTIFICATIONS, {
    variables: {
      location: !isAdmin ? user?.location?.id : undefined,
    },
    skip: !open,
  });

  const {
    data: locationsData } = useQuery(GET_LOCATIONS, {
    variables:{
      params: { page: 1,
        pageSize: 500 },
    },
  });

  const locations = locationsData?.locations?.results ?? [];

  const [createDoc] = useMutation(CREATE_EMAILS_NOTIFICATION, {
    onCompleted: () => refetch(),
  });

  const [updateDoc] = useMutation(UPDATE_EMAILS_NOTIFICATION, {
    onCompleted: () => refetch(),
  });

  const columns = useMemo(
    () => [
      {
        title: "TO",
        dataIndex: "to",
      },
      {
        title: "CC",
        render: (_: any, record: any) =>
          record.cc?.length ? record.cc.join(", ") : "-",
      },
      {
        title: "Predeterminado",
        dataIndex: "isDefault",
        render: (value: boolean) => (value ? "Sí" : "No"),
      },
      isAdmin && {
        title: "Sucursal",
        render: (_: any, record: any) =>
          record.location?.name ?? "N/A",
      },
      {
        title: "Acciones",
        key: "actions",
        render: (_: any, record: any) => (
          <Space>
            <Button
              size="small"
              onClick={() => {
                setEditingId(record.id);
                setShowCreateForm(true);
                setForm({
                  to: record.to ?? "",
                  cc: record.cc?.join(", ") ?? "",
                  isDefault: record.isDefault ?? false,
                  location: record.location?.id ?? null,
                });
              }}
              icon={<EditOutlined />}
            />

            <Popconfirm
              title="¿Eliminar configuración?"
              onConfirm={() =>
                updateDoc({
                  variables: {
                    id: record.id,
                    emailsNotification: { deleted: true },
                  },
                })}
            >
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ].filter(Boolean),
    [isAdmin],
  );

  return (
    <Drawer
      title="Correos de notificación"
      open={open}
      onClose={() => {
        setOpen(false);
        setShowCreateForm(false);
        setEditingId(null);
      }}
      width="75%"
      destroyOnClose
    >
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          onClick={() => {
            setEditingId(null);
            setShowCreateForm(true);
            setForm({
              to: "",
              cc: "",
              isDefault: false,
              location: isAdmin
                ? null
                : user?.location?.id ?? null,
            });
          }}
        >
          Nuevo
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={data?.emailsNotifications?.results ?? []}
        columns={columns as any}
        pagination={{
          current: Number(pageConfig.page),
          pageSize: Number(pageConfig.pageSize),
          total: data?.emailsNotifications?.info?.count,
          onChange: (page, pageSize) =>
            setPageConfig({ page,
              pageSize }),
          showSizeChanger: true,
        }}
      />

      <Text type="secondary" style={{ fontSize: 12 }}>
        Cada fila representa una configuración completa de envío
      </Text>

      {showCreateForm && (
        <div style={{ marginTop: 24 }}>
          <Text strong style={{ display: "block",
            marginBottom: 12 }}>
            {editingId
              ? "Editar configuración"
              : "Nueva configuración"}
          </Text>

          <Space direction="vertical" style={{ width: "100%" }}>
            <Input
              placeholder="Correo TO"
              value={form.to}
              onChange={(e) =>
                setForm({ ...form,
                  to: e.target.value })}
            />

            <Input
              placeholder="Correos CC (separados por coma)"
              value={form.cc}
              onChange={(e) =>
                setForm({ ...form,
                  cc: e.target.value })}
            />

            <Row>
              <Switch
                checked={form.isDefault}
                onChange={(value) =>
                  setForm({ ...form,
                    isDefault: value })}
                checkedChildren="Predeterminado"
                unCheckedChildren="No predeterminado"
              />
              <Text type="secondary" style={{ fontSize: 12,
                marginLeft: 8 }}>
                Los correos predeterminados se enviarán desde cualquier sucursal que no tenga una configuración específica
              </Text>
            </Row>


            {isAdmin && (
              <Select
                placeholder="Sucursal"
                style={{ width: "80%" }}
                allowClear
                value={form.location}
                onChange={(value) =>
                  setForm({ ...form,
                    location: value })}
                options={locations.map((l: any) => ({
                  label: l.name,
                  value: l.id,
                }))}
              />
            )}

            <Space>
              <Button
                type="primary"
                disabled={!form.to}
                onClick={async () => {
                  const payload = {
                    to: form.to,
                    cc: form.cc
                      .split(",")
                      .map((e) => e.trim())
                      .filter(Boolean),
                    isDefault: form.isDefault,
                    location: form.location,
                  };

                  if (editingId) {
                    await updateDoc({
                      variables: {
                        id: editingId,
                        emailsNotification: payload,
                      },
                    });
                    message.success(
                      "Configuración actualizada",
                    );
                  } else {
                    await createDoc({
                      variables: {
                        emailsNotification: payload,
                      },
                    });
                    message.success("Configuración creada");
                  }

                  setShowCreateForm(false);
                  setEditingId(null);
                }}
              >
                Guardar
              </Button>

              <Button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingId(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Space>
        </div>
      )}
    </Drawer>
  );
};

export default EmailsNotificationsDrawer;
